import React, { useEffect, useState } from 'react'
import { Alert, Button, Col, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { ALLOW_IN_MVP } from '../../config/environment'
import { correspondentService } from '../../services/correspondent.service'
import { documentTypeService } from '../../services/document-type.service'
import { mailAccountService } from '../../services/mail-account.service'
import { mailRuleService } from '../../services/mail-rule.service'
import { toastService } from '../../services/toast.service'
import {
  MailAccount,
  MailAction,
  MailFilterAttachmentType,
  MailMetadataCorrespondentOption,
  MailMetadataTitleOption,
  MailRule,
  MailRuleConsumptionScope,
  MailRulePdfLayout,
} from '../../types'
import { parseBackendErrors } from '../../utils/errorHandler'
import { DocumentationDialog } from '../common/DocumentationDialog'
import { InputSelect } from '../common/InputSelect'
import { InputSwitch } from '../common/InputSwitch'
import { InputTags } from '../common/InputTags'
import { InputText } from '../common/InputText'
import Phase2NoticeModal from '../common/Phase2NoticeModal'

interface MailRuleEditDialogProps {
  show: boolean
  onHide: () => void
  rule?: MailRule
  onSave: () => void
  readOnly?: boolean
}

export const MailRuleEditDialog: React.FC<MailRuleEditDialogProps> = ({ show, onHide, rule, onSave, readOnly = false }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<Partial<MailRule>>({
    name: null,
    account: null,
    order: 0,
    enabled: true,
    folder: 'INBOX',
    filter_from: null,
    filter_to: null,
    filter_subject: null,
    filter_body: null,
    filter_attachment_filename_include: null,
    filter_attachment_filename_exclude: null,
    maximum_age: null,
    consumption_scope: MailRuleConsumptionScope.Attachments,
    attachment_type: MailFilterAttachmentType.Attachments,
    pdf_layout: MailRulePdfLayout.Default,
    action: MailAction.MarkRead,
    action_parameter: null,
    assign_title_from: MailMetadataTitleOption.FromSubject,
    assign_tags: [],
    assign_document_type: null,
    assign_correspondent_from: MailMetadataCorrespondentOption.FromNothing,
    assign_correspondent: null,
    assign_owner_from_rule: true,
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [accounts, setAccounts] = useState<MailAccount[]>([])
  const [correspondents, setCorrespondents] = useState<any[]>([])
  const [documentTypes, setDocumentTypes] = useState<any[]>([])
  const [showDocsDialog, setShowDocsDialog] = useState(false)
  const [showTotpPhase2Modal, setShowTotpPhase2Modal] = useState(false)

  const consumptionScopeOptions = [
    { value: MailRuleConsumptionScope.Attachments, label: t('process_attachments_only') },
    { value: MailRuleConsumptionScope.EmailOnly, label: t('process_email_only') },
    { value: MailRuleConsumptionScope.Everything, label: t('process_everything') },
  ]

  const attachmentTypeOptions = [
    { value: MailFilterAttachmentType.Attachments, label: t('only_process_attachments') },
    { value: MailFilterAttachmentType.Everything, label: t('process_all_files_including_inline_attachments') },
  ]

  const pdfLayoutOptions = [
    { value: MailRulePdfLayout.Default, label: t('system_default') },
    { value: MailRulePdfLayout.TextHtml, label: t('text_then_html') },
    { value: MailRulePdfLayout.HtmlText, label: t('html_then_text') },
    { value: MailRulePdfLayout.HtmlOnly, label: t('html_only') },
    { value: MailRulePdfLayout.TextOnly, label: t('text_only') },
  ]

  const actionOptions = [
    { value: MailAction.Delete, label: t('delete') },
    { value: MailAction.Move, label: t('move_to_specified_folder') },
    { value: MailAction.MarkRead, label: t('mark_as_read_dont_process_read_mails') },
    { value: MailAction.Flag, label: t('flag_mail_dont_process_flagged_mails') },
    { value: MailAction.Tag, label: t('tag_mail_with_specified_tag_dont_process_tagged_mails') },
  ]

  const metadataTitleOptions = [
    { value: MailMetadataTitleOption.FromSubject, label: t('use_subject_as_title') },
    { value: MailMetadataTitleOption.FromFilename, label: t('use_filename_as_title') },
    { value: MailMetadataTitleOption.None, label: t('do_not_assign_title') },
  ]

  const metadataCorrespondentOptions = [
    { value: MailMetadataCorrespondentOption.FromNothing, label: t('do_not_assign_correspondent') },
    { value: MailMetadataCorrespondentOption.FromEmail, label: t('use_mail_address') },
    { value: MailMetadataCorrespondentOption.FromName, label: t('use_name_or_mail_address') },
    { value: MailMetadataCorrespondentOption.FromCustom, label: t('use_correspondent_selected_below') },
  ]

  useEffect(() => {
    if (show) {
      setErrors({})
      loadData()
    }
  }, [show])

  useEffect(() => {
    if (rule) {
      setFormData(rule)
    } else {
      setFormData({
        name: null,
        account: null,
        order: 0,
        enabled: true,
        folder: 'INBOX',
        filter_from: null,
        filter_to: null,
        filter_subject: null,
        filter_body: null,
        filter_attachment_filename_include: null,
        filter_attachment_filename_exclude: null,
        maximum_age: null,
        consumption_scope: MailRuleConsumptionScope.Attachments,
        attachment_type: MailFilterAttachmentType.Attachments,
        pdf_layout: MailRulePdfLayout.Default,
        action: MailAction.MarkRead,
        action_parameter: null,
        assign_title_from: MailMetadataTitleOption.FromSubject,
        assign_tags: [],
        assign_document_type: null,
        assign_correspondent_from: MailMetadataCorrespondentOption.FromNothing,
        assign_correspondent: null,
        assign_owner_from_rule: true,
      })
    }
  }, [rule, show])

  const loadData = async () => {
    try {
      const [accountsRes, correspondentsRes, documentTypesRes] = await Promise.all([
        mailAccountService.getMailAccounts(),
        correspondentService.listAll(),
        documentTypeService.listAll(),
      ])

      setAccounts(accountsRes.results)
      setCorrespondents(correspondentsRes.results)
      setDocumentTypes(documentTypesRes.results)
    } catch (error) {}
  }

  const handleInputChange = (field: keyof MailRule, value: any) => {
    setFormData((prev) => {
      let processedValue = value

      if (typeof value === 'string' && value.trim() === '') {
        processedValue = null
      } else if (field === 'account' && (value === 0 || value === null || value === undefined || value === '')) {
        processedValue = null
      } else if (field === 'order' && (value === '' || value === undefined || value === null)) {
        processedValue = null
      } else if (field === 'maximum_age' && (value === '' || value === undefined || value === null)) {
        processedValue = null
      } else if (field === 'action_parameter') {
        if (value === '' || value === undefined || value === null) {
          processedValue = null
        } else if (prev.action !== MailAction.Move && prev.action !== MailAction.Tag) {
          processedValue = null
        }
      } else if (field === 'assign_document_type' && (value === 0 || value === null || value === undefined)) {
        processedValue = null
      } else if (field === 'assign_correspondent' && (value === 0 || value === null || value === undefined)) {
        processedValue = null
      }

      return {
        ...prev,
        [field]: processedValue,
      }
    })
  }

  const prepareFormData = (data: Partial<MailRule>): Partial<MailRule> => {
    const prepared: any = { ...data }

    const stringFields = [
      'name',
      'filter_from',
      'filter_to',
      'filter_subject',
      'filter_body',
      'filter_attachment_filename_include',
      'filter_attachment_filename_exclude',
      'action_parameter',
    ]

    stringFields.forEach((field) => {
      if (prepared[field] === '' || prepared[field] === undefined) {
        prepared[field] = null
      }
    })

    if (prepared.account === 0 || prepared.account === undefined || prepared.account === null || prepared.account === '') {
      prepared.account = null
    }

    if (prepared.order === undefined || prepared.order === null || prepared.order === '') {
      prepared.order = null
    }

    if (prepared.maximum_age === undefined || prepared.maximum_age === null || prepared.maximum_age === '') {
      prepared.maximum_age = null
    }

    if (prepared.action !== MailAction.Move && prepared.action !== MailAction.Tag) {
      prepared.action_parameter = null
    }

    if (prepared.assign_tags && Array.isArray(prepared.assign_tags) && prepared.assign_tags.length === 0) {
      prepared.assign_tags = []
    }

    if (prepared.assign_document_type === undefined || prepared.assign_document_type === 0 || prepared.assign_document_type === null) {
      prepared.assign_document_type = null
    }

    if (prepared.assign_correspondent === undefined || prepared.assign_correspondent === 0 || prepared.assign_correspondent === null) {
      prepared.assign_correspondent = null
    }

    return prepared
  }

  const handleSave = async () => {
    if (ALLOW_IN_MVP) {
      setShowTotpPhase2Modal(true)
    } else {
      setLoading(true)
      setErrors({})

      try {
        const dataToSave = prepareFormData(formData)

        if (rule) {
          await mailRuleService.updateMailRule(rule.id, dataToSave)
          toastService.showSuccess(t('mail_rule_updated', { name: dataToSave.name }))
        } else {
          await mailRuleService.createMailRule(dataToSave)
          toastService.showSuccess(t('mail_rule_created', { name: dataToSave.name }))
        }
        onSave()
        onHide()
      } catch (error: any) {
        const { fieldErrors, errorResponse } = parseBackendErrors(error, t, t('error_saving_mail_rule'))

        setErrors(fieldErrors)
        toastService.showError(t('error_saving_mail_rule'), errorResponse)
      } finally {
        setLoading(false)
      }
    }
  }

  const getTitle = () => {
    return rule ? t('edit_mail_rule', { name: rule.name }) : t('create_mail_rule')
  }

  const showActionParamField = formData.action === MailAction.Move || formData.action === MailAction.Tag
  const showCorrespondentField = formData.assign_correspondent_from === MailMetadataCorrespondentOption.FromCustom

  const FieldHelp = ({ children }: { children: React.ReactNode }) => (
    <small className="text-muted d-block mt-1 mb-2" style={{ fontSize: '0.875rem' }}>
      {children}
    </small>
  )

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {getTitle()}
          {rule && <span className="badge bg-primary ms-2">ID: {rule.id}</span>}
        </Modal.Title>
        <Button variant="link" size="sm" className="text-muted me-auto p-0 p-md-2" title={t('whats_this')} onClick={() => setShowDocsDialog(true)}>
          <i className="bi bi-info-circle"></i>
        </Button>
      </Modal.Header>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (readOnly) return
          handleSave()
        }}
      >
        <Modal.Body>
          <fieldset
            disabled={readOnly}
            aria-disabled={readOnly}
            className="border-0 p-0 m-0"
            style={readOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}
          >
          {errors._general && (
            <Alert variant="danger" className="mb-3">
              {errors._general}
            </Alert>
          )}
          <Row className="mb-3">
            <Col md={4}>
              <InputText
                title={t('name')}
                value={formData.name ?? ''}
                onChange={(value) => handleInputChange('name', value)}
                error={errors.name}
                placeholder={t('enter_name')}
              />
              <FieldHelp>{t('mail_rule_name_help')}</FieldHelp>
            </Col>
            <Col md={4}>
              <InputSelect
                title={t('account')}
                value={formData.account}
                onChange={(value) => handleInputChange('account', value)}
                items={accounts}
                bindLabel="name"
                allowNull={true}
                placeholder={t('select_account')}
              />
              {errors.account && <div className="text-danger small mt-1">{errors.account}</div>}
              <FieldHelp>{t('mail_rule_account_help')}</FieldHelp>
            </Col>
            <Col md={2}>
              <InputText
                title={t('order')}
                type="number"
                value={formData.order ?? ''}
                onChange={(value) => handleInputChange('order', value === '' ? null : parseInt(value) || null)}
                error={errors.order}
              />
              <FieldHelp>{t('mail_rule_order_help')}</FieldHelp>
            </Col>
            <Col md={2} className="pt-4">
              <InputSwitch
                title={t('enabled')}
                value={formData.enabled || false}
                onChange={(value) => handleInputChange('enabled', value)}
                horizontal={true}
              />
              <FieldHelp>{t('mail_rule_enabled_help')}</FieldHelp>
            </Col>
          </Row>

          <hr />
          <p className="small text-muted mb-3">
            <em>{t('mail_rule_criteria_info')}</em>
          </p>

          <Row>
            <Col md={6}>
              <InputText
                title={t('folder')}
                value={formData.folder ?? 'INBOX'}
                onChange={(value) => handleInputChange('folder', value)}
                hint={t('folder_hint')}
                error={errors.folder}
              />
              <FieldHelp>{t('mail_rule_folder_help')}</FieldHelp>

              <InputText
                title={t('maximum_age_days')}
                type="number"
                value={formData.maximum_age ?? ''}
                onChange={(value) => handleInputChange('maximum_age', value === '' ? null : parseInt(value) || null)}
                error={errors.maximum_age}
              />
              <FieldHelp>{t('mail_rule_maximum_age_help')}</FieldHelp>
            </Col>

            <Col md={6}>
              <InputText
                title={t('filter_from')}
                value={formData.filter_from ?? ''}
                onChange={(value) => handleInputChange('filter_from', value)}
                error={errors.filter_from}
              />
              <FieldHelp>{t('mail_rule_filter_from_help')}</FieldHelp>

              <InputText
                title={t('filter_to')}
                value={formData.filter_to ?? ''}
                onChange={(value) => handleInputChange('filter_to', value)}
                error={errors.filter_to}
              />
              <FieldHelp>{t('mail_rule_filter_to_help')}</FieldHelp>

              <InputText
                title={t('filter_subject')}
                value={formData.filter_subject ?? ''}
                onChange={(value) => handleInputChange('filter_subject', value)}
                error={errors.filter_subject}
              />
              <FieldHelp>{t('mail_rule_filter_subject_help')}</FieldHelp>

              <InputText
                title={t('filter_body')}
                value={formData.filter_body ?? ''}
                onChange={(value) => handleInputChange('filter_body', value)}
                error={errors.filter_body}
              />
              <FieldHelp>{t('mail_rule_filter_body_help')}</FieldHelp>
            </Col>
          </Row>

          <hr />

          <Row>
            <Col md={6}>
              <InputSelect
                title={t('consumption_scope')}
                value={formData.consumption_scope || MailRuleConsumptionScope.Attachments}
                onChange={(value) => handleInputChange('consumption_scope', value)}
                items={consumptionScopeOptions.map((option) => ({ id: option.value, name: option.label }))}
                bindLabel="name"
                hint={t('consumption_scope_hint')}
              />
              <FieldHelp>{t('mail_rule_consumption_scope_help')}</FieldHelp>

              <InputSelect
                title={t('attachment_type')}
                value={formData.attachment_type || MailFilterAttachmentType.Attachments}
                onChange={(value) => handleInputChange('attachment_type', value)}
                items={attachmentTypeOptions.map((option) => ({ id: option.value, name: option.label }))}
                bindLabel="name"
              />
              <FieldHelp>{t('mail_rule_attachment_type_help')}</FieldHelp>

              <InputSelect
                title={t('pdf_layout')}
                value={formData.pdf_layout || MailRulePdfLayout.Default}
                onChange={(value) => handleInputChange('pdf_layout', value)}
                items={pdfLayoutOptions.map((option) => ({ id: option.value, name: option.label }))}
                bindLabel="name"
              />
              <FieldHelp>{t('mail_rule_pdf_layout_help')}</FieldHelp>
            </Col>

            <Col md={6}>
              <InputText
                title={t('include_only_files_matching')}
                value={formData.filter_attachment_filename_include ?? ''}
                onChange={(value) => handleInputChange('filter_attachment_filename_include', value)}
                hint={t('include_files_hint')}
                error={errors.filter_attachment_filename_include}
              />
              <FieldHelp>{t('mail_rule_include_files_help')}</FieldHelp>

              <InputText
                title={t('exclude_files_matching')}
                value={formData.filter_attachment_filename_exclude ?? ''}
                onChange={(value) => handleInputChange('filter_attachment_filename_exclude', value)}
                hint={t('exclude_files_hint')}
                error={errors.filter_attachment_filename_exclude}
              />
              <FieldHelp>{t('mail_rule_exclude_files_help')}</FieldHelp>
            </Col>
          </Row>

          <hr />

          <Row>
            <Col md={6}>
              <InputSelect
                title={t('action')}
                value={formData.action || MailAction.MarkRead}
                onChange={(value) => handleInputChange('action', value)}
                items={actionOptions.map((option) => ({ id: option.value, name: option.label }))}
                bindLabel="name"
                hint={t('action_hint')}
              />
              <FieldHelp>{t('mail_rule_action_help')}</FieldHelp>

              {showActionParamField && (
                <>
                  <InputText
                    title={t('action_parameter')}
                    value={formData.action_parameter ?? ''}
                    onChange={(value) => handleInputChange('action_parameter', value)}
                    error={errors.action_parameter}
                    placeholder={formData.action === MailAction.Move ? t('folder') : t('tag')}
                  />
                  <FieldHelp>
                    {formData.action === MailAction.Move ? t('mail_rule_action_parameter_move_help') : t('mail_rule_action_parameter_tag_help')}
                  </FieldHelp>
                </>
              )}
              {!showActionParamField && errors.action_parameter && (
                <Alert variant="danger" className="mt-2">
                  {t('action_parameter')}: {errors.action_parameter}
                </Alert>
              )}

              <InputSelect
                title={t('assign_title_from')}
                value={formData.assign_title_from || MailMetadataTitleOption.FromSubject}
                onChange={(value) => handleInputChange('assign_title_from', value)}
                items={metadataTitleOptions.map((option) => ({ id: option.value, name: option.label }))}
                bindLabel="name"
              />
              <FieldHelp>{t('mail_rule_assign_title_help')}</FieldHelp>

              <InputSwitch
                title={t('assign_owner_from_rule')}
                value={formData.assign_owner_from_rule || false}
                onChange={(value) => handleInputChange('assign_owner_from_rule', value)}
                horizontal={true}
              />
              <FieldHelp>{t('mail_rule_assign_owner_help')}</FieldHelp>
            </Col>

            <Col md={6}>
              <InputTags
                title={t('tags')}
                value={formData.assign_tags || []}
                onChange={(value) => handleInputChange('assign_tags', value)}
                multiple={true}
              />
              <FieldHelp>{t('mail_rule_tags_help')}</FieldHelp>

              <InputSelect
                title={t('assign_document_type')}
                value={formData.assign_document_type}
                onChange={(value) => handleInputChange('assign_document_type', value)}
                items={documentTypes}
                bindLabel="name"
                allowNull={true}
                placeholder={t('do_not_assign_document_type')}
              />
              <FieldHelp>{t('mail_rule_document_type_help')}</FieldHelp>

              <InputSelect
                title={t('assign_correspondent_from')}
                value={formData.assign_correspondent_from ?? MailMetadataCorrespondentOption.FromNothing}
                onChange={(value) => handleInputChange('assign_correspondent_from', value)}
                items={metadataCorrespondentOptions.map((option) => ({ id: option.value, name: option.label }))}
                bindLabel="name"
              />
              <FieldHelp>{t('mail_rule_assign_correspondent_from_help')}</FieldHelp>

              {showCorrespondentField && (
                <>
                  <InputSelect
                    title={t('assign_correspondent')}
                    value={formData.assign_correspondent}
                    onChange={(value) => handleInputChange('assign_correspondent', value)}
                    items={correspondents}
                    bindLabel="name"
                    allowNull={true}
                    placeholder={t('select_correspondent')}
                  />
                  <FieldHelp>{t('mail_rule_assign_correspondent_help')}</FieldHelp>
                </>
              )}
            </Col>
          </Row>
          </fieldset>
        </Modal.Body>

        <Modal.Footer>
          <Button type="button" variant="outline-secondary" onClick={onHide} disabled={loading}>
            {t('cancel')}
          </Button>

          <Button
            type="submit"
            variant="primary"
            disabled={loading || readOnly}
            title={readOnly ? t('you_dont_have_permissions_to_do_that') : undefined}
          >
            {loading && (
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">{t('loading')}...</span>
              </div>
            )}
            {t('save')}
          </Button>
        </Modal.Footer>
      </form>

      <DocumentationDialog
        show={showDocsDialog}
        onHide={() => setShowDocsDialog(false)}
        docKey="mail"
        sectionTitles={['Mail Rules', 'Mail Rule Actions', 'Pravila Pošte', 'Akcije Pravila Pošte']}
      />
      <Phase2NoticeModal show={showTotpPhase2Modal} onClose={() => setShowTotpPhase2Modal(false)} />
    </Modal>
  )
}
